export interface IndustryPageData {
  slug: string;
  industry: string;
  headline: string;
  subheadline: string;
  metaTitle: string;
  metaDescription: string;
  problemStats: Array<{
    value: string;
    label: string;
  }>;
  problemHeadline: string;
  problemDescription: string;
  capabilities: Array<{
    title: string;
    description: string;
    details: string[];
  }>;
  productExample: {
    productType: string;
    steps: Array<{
      title: string;
      description: string;
    }>;
  };
  relatedBlogSlugs: string[];
  relatedProductSlugs: string[];
  ctaHeadline: string;
  ctaDescription: string;
}

export const industryPages: IndustryPageData[] = [
  {
    slug: 'power-tools',
    industry: 'Power Tools',
    headline: 'Your tradespeople scan it once. You keep the relationship forever.',
    subheadline: 'BrandedMark turns every power tool into a registered, supported, reorderable product — from first scan to tenth consumable order. One QR code replaces paper warranty cards, lost manuals, and missed registration windows.',
    metaTitle: 'Power Tool Warranty & Product Registration Platform | BrandedMark',
    metaDescription: 'BrandedMark helps power tool manufacturers capture warranty registrations and drive consumable reorders. Turn every tool into a connected product with QR-based identity.',
    problemStats: [
      { value: '70%', label: 'of power tool warranties are never registered' },
      { value: '4 weeks', label: 'average registration window before warranty lapses' },
      { value: '£0', label: 'aftermarket revenue from unregistered tool owners' },
    ],
    problemHeadline: 'Your best customers are invisible',
    problemDescription: 'A tradesperson buys your drill, uses it every day for five years, and buys consumables monthly. But because they never registered the warranty, you don\'t know they exist. Their consumable spend goes to Amazon, not to you. Their next tool purchase goes to whoever shows up first.',
    capabilities: [
      {
        title: 'Warranty registration at point of sale',
        description: 'A QR code on the tool or packaging. The tradesperson scans it on-site — 30 seconds, no app download, no serial number lookup. Warranty activated, customer captured, relationship started.',
        details: [
          'QR scan-to-register in under 30 seconds',
          'No app required — works in any mobile browser',
          'Serial number auto-detected from QR — no manual entry',
          'Registration confirmation with digital warranty card',
        ],
      },
      {
        title: 'Consumable and spare parts reordering',
        description: 'Every registered tool links to its compatible consumables — blades, bits, batteries, chargers. When the tradesperson needs a replacement, they scan the QR on the tool and order directly from you.',
        details: [
          'Compatible parts linked to exact model and serial number',
          'Direct ordering — no searching catalogues or calling distributors',
          'Captures aftermarket revenue lost to third-party sellers',
          'Usage data shows which consumables need restocking',
        ],
      },
      {
        title: 'Digital product identity for every unit',
        description: 'Each tool gets a unique digital identity — not just per-SKU, but per-unit. Serial number, manufacture date, firmware version, warranty status, service history — all in one scannable QR code.',
        details: [
          'Per-unit serialised identity (not just SKU-level)',
          'GS1 Digital Link compliant for retail and supply chain',
          'Works with QR codes and NFC tags',
          'Product history follows the tool across its entire lifecycle',
        ],
      },
      {
        title: 'EU Right to Repair and DPP compliance',
        description: 'The EU Right to Repair directive requires spare parts availability for 7-10 years and transparent product information. BrandedMark gives every tool a digital product passport that satisfies compliance requirements from the same QR code that handles registration.',
        details: [
          'Spare parts catalogue accessible per-product via QR',
          'Repair history tracked and verifiable',
          'Digital Product Passport data structure built in',
          'Ready for EU ESPR requirements as they roll out',
        ],
      },
    ],
    productExample: {
      productType: 'cordless drill',
      steps: [
        { title: 'Tradesperson buys drill', description: 'Purchases from Screwfix, Toolstation, or direct. QR code is on the box and on the tool itself.' },
        { title: 'Scans QR on-site', description: '30-second registration. No app needed. Warranty activated. You now know who owns this specific drill.' },
        { title: 'Needs new battery at month 6', description: 'Scans the QR again — sees compatible batteries for this exact model. Orders direct from you.' },
        { title: 'Drill handed to apprentice at year 3', description: 'Ownership transfers via QR scan. New owner inherits remaining warranty and full service history.' },
      ],
    },
    relatedBlogSlugs: [
      'why-warranty-registration-fails',
      'digital-product-identity-guide',
      'what-is-post-purchase-operating-system',
    ],
    relatedProductSlugs: ['warranty', 'qr-nfc-tags', 'spares-accessories', 'compliance-dpp'],
    ctaHeadline: 'Ready to connect your power tools?',
    ctaDescription: 'Join the waitlist to give every tool a digital identity — warranty registration, spare parts, and compliance from a single QR code.',
  },
];

export function getIndustryPage(slug: string): IndustryPageData | undefined {
  return industryPages.find(p => p.slug === slug);
}
